import * as THREE from "three";
import { CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import { PLANET_RADIUS } from "./sphere";

const GRASS = 0xa8d46a;
const GRASS_DARK = 0x8fbf55;
const PATH = 0xe3d8c6;
const PATH_EDGE = 0xcfc0a6;
const CLOUD = 0xfbfcfd;
const SKY = 0xeef0ea;

export function createRenderer(host: HTMLElement): {
  renderer: THREE.WebGLRenderer;
  labels: CSS2DRenderer;
} {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(host.clientWidth, host.clientHeight);
  renderer.setClearColor(SKY, 0);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.18;
  host.appendChild(renderer.domElement);

  const labels = new CSS2DRenderer();
  labels.setSize(host.clientWidth, host.clientHeight);
  labels.domElement.style.position = "absolute";
  labels.domElement.style.inset = "0";
  labels.domElement.style.pointerEvents = "none";
  host.appendChild(labels.domElement);

  return { renderer, labels };
}

export function createLights(scene: THREE.Scene): void {
  const hemi = new THREE.HemisphereLight(0xffffff, 0xb7d99a, 1.35);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xfff6e5, 1.6);
  sun.position.set(30, 46, 22);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.near = 5;
  sun.shadow.camera.far = 140;
  sun.shadow.camera.left = -40;
  sun.shadow.camera.right = 40;
  sun.shadow.camera.top = 40;
  sun.shadow.camera.bottom = -40;
  sun.shadow.bias = -0.0005;
  sun.shadow.radius = 4;
  scene.add(sun);
  const fill = new THREE.DirectionalLight(0xdfe8ff, 0.45);
  fill.position.set(-30, 10, -25);
  scene.add(fill);
  scene.add(new THREE.AmbientLight(0xffffff, 0.35));
  scene.fog = new THREE.Fog(SKY, 42, 90);
}

/** 浅绿分面玩具星球 + 石板环路 */
export function createPlanet(): THREE.Group {
  const root = new THREE.Group();
  root.name = "toy-planet";

  const geo = new THREE.IcosahedronGeometry(PLANET_RADIUS, 4);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const cGrass = new THREE.Color(GRASS);
  const cDark = new THREE.Color(GRASS_DARK);
  const cPath = new THREE.Color(PATH);
  const tmp = new THREE.Vector3();

  for (let i = 0; i < pos.count; i++) {
    tmp.fromBufferAttribute(pos, i).normalize();
    // slight facet noise
    const n = (Math.sin(tmp.x * 9.1) + Math.cos(tmp.y * 7.3) + Math.sin(tmp.z * 5.7)) * 0.08;
    tmp.multiplyScalar(PLANET_RADIUS * (1 + n * 0.015));
    pos.setXYZ(i, tmp.x, tmp.y, tmp.z);

    const lon = Math.atan2(tmp.z, tmp.x);
    const lat = Math.asin(THREE.MathUtils.clamp(tmp.y / PLANET_RADIUS, -1, 1));
    // path bands: latitude rings + a couple meridians
    const onLatPath = Math.abs(Math.sin(lat * 3)) < 0.12 && Math.abs(lat) < 0.85;
    const onLonPath = Math.abs(Math.sin(lon * 2)) < 0.08 && Math.abs(lat) < 0.7;
    const col = onLatPath || onLonPath ? cPath : n > 0.06 ? cDark : cGrass;
    colors[i * 3] = col.r;
    colors[i * 3 + 1] = col.g;
    colors[i * 3 + 2] = col.b;
  }
  pos.needsUpdate = true;
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();

  const mesh = new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.85,
      metalness: 0,
      flatShading: true,
    }),
  );
  mesh.receiveShadow = true;
  mesh.castShadow = true;
  root.add(mesh);

  // soft underglow disk (toy base feel)
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(PLANET_RADIUS * 1.5, 48),
    new THREE.MeshBasicMaterial({ color: 0xcfd6c8, transparent: true, opacity: 0.22, fog: false }),
  );
  disc.rotation.x = -Math.PI / 2;
  disc.position.y = -PLANET_RADIUS - 0.4;
  root.add(disc);

  scatterTrees(root);
  scatterFlowers(root);
  scatterRocks(root);
  scatterMushrooms(root);
  scatterGrassTufts(root);
  addPond(root);
  addPathMarkers(root);
  return root;
}

function scatterRocks(root: THREE.Group): void {
  const mats = [0xb0bec5, 0x90a4ae, 0x8d8d8d].map(
    (c) => new THREE.MeshStandardMaterial({ color: c, flatShading: true, roughness: 0.95 }),
  );
  for (let i = 0; i < 26; i++) {
    const lat = (Math.random() - 0.5) * 1.7;
    const lon = Math.random() * Math.PI * 2;
    if (Math.abs(lat) < 0.07) continue;
    const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(0.12 + Math.random() * 0.14, 0), mats[i % mats.length]);
    rock.rotation.set(Math.random() * 3, Math.random() * 3, Math.random() * 3);
    rock.castShadow = true;
    placeOnSphere(rock, lat, lon, 0.05);
    root.add(rock);
  }
}

function scatterMushrooms(root: THREE.Group): void {
  const stemMat = new THREE.MeshStandardMaterial({ color: 0xf5f0e1, flatShading: true });
  const capMats = [0xe57373, 0xffb74d].map(
    (c) => new THREE.MeshStandardMaterial({ color: c, flatShading: true, roughness: 0.85 }),
  );
  for (let i = 0; i < 18; i++) {
    const lat = (Math.random() - 0.5) * 1.5;
    const lon = Math.random() * Math.PI * 2;
    if (Math.abs(lat) < 0.07) continue;
    const g = new THREE.Group();
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.05, 0.14, 5), stemMat);
    stem.position.y = 0.07;
    const cap = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.1, 7), capMats[i % capMats.length]);
    cap.position.y = 0.17;
    g.add(stem, cap);
    g.scale.setScalar(0.8 + Math.random() * 0.6);
    placeOnSphere(g, lat, lon, 0);
    root.add(g);
  }
}

function scatterGrassTufts(root: THREE.Group): void {
  const mat = new THREE.MeshStandardMaterial({ color: 0x7cb342, flatShading: true, roughness: 1 });
  for (let i = 0; i < 90; i++) {
    const lat = (Math.random() - 0.5) * 1.75;
    const lon = Math.random() * Math.PI * 2;
    if (Math.abs(lat) < 0.06) continue;
    const tuft = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.16, 4), mat);
    tuft.rotation.y = Math.random() * Math.PI;
    placeOnSphere(tuft, lat, lon, 0.05);
    root.add(tuft);
  }
}

function addPond(root: THREE.Group): void {
  const pond = new THREE.Mesh(
    new THREE.CircleGeometry(1.6, 24),
    new THREE.MeshStandardMaterial({
      color: 0x90caf9,
      roughness: 0.35,
      metalness: 0.05,
      transparent: true,
      opacity: 0.92,
    }),
  );
  pond.receiveShadow = true;
  placeOnSphere(pond, -0.5, 2.2, 0.03);
  // placeOnSphere orients +Y to surface normal; circle faces +Z, so rotate to lie flat
  pond.rotateX(-Math.PI / 2);
  root.add(pond);
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(1.6, 0.07, 6, 28),
    new THREE.MeshStandardMaterial({ color: 0xd7cbb8, flatShading: true, roughness: 0.95 }),
  );
  placeOnSphere(rim, -0.5, 2.2, 0.03);
  rim.rotateX(Math.PI / 2);
  root.add(rim);
}

function scatterTrees(root: THREE.Group): void {
  const pineMat = new THREE.MeshStandardMaterial({ color: 0x4caf50, flatShading: true, roughness: 0.9 });
  const pineDark = new THREE.MeshStandardMaterial({ color: 0x388e3c, flatShading: true, roughness: 0.9 });
  const trunkMat = new THREE.MeshStandardMaterial({ color: 0x8d6e63, flatShading: true });
  const roundMat = new THREE.MeshStandardMaterial({ color: 0x66bb6a, flatShading: true });
  const blossomMat = new THREE.MeshStandardMaterial({ color: 0xf8bbd0, flatShading: true });

  for (let i = 0; i < 84; i++) {
    const lat = (Math.random() - 0.5) * 1.7;
    const lon = Math.random() * Math.PI * 2;
    const kind = i % 4 === 0 ? "round" : i % 7 === 0 ? "blossom" : "pine";
    const tree =
      kind === "pine"
        ? makePine(pineMat, pineDark, trunkMat)
        : makeRoundTree(kind === "blossom" ? blossomMat : roundMat, trunkMat);
    placeOnSphere(tree, lat, lon, 0);
    if (Math.abs(lat) < 0.06) continue;
    root.add(tree);
  }
}

function makePine(
  a: THREE.MeshStandardMaterial,
  b: THREE.MeshStandardMaterial,
  trunk: THREE.MeshStandardMaterial,
): THREE.Group {
  const g = new THREE.Group();
  const t = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 0.35, 5), trunk);
  t.position.y = 0.18;
  t.castShadow = true;
  const c1 = new THREE.Mesh(new THREE.ConeGeometry(0.38, 0.55, 6), a);
  c1.position.y = 0.55;
  c1.castShadow = true;
  const c2 = new THREE.Mesh(new THREE.ConeGeometry(0.28, 0.45, 6), b);
  c2.position.y = 0.85;
  c2.castShadow = true;
  g.add(t, c1, c2);
  const s = 0.75 + Math.random() * 0.55;
  g.scale.setScalar(s);
  return g;
}

function makeRoundTree(leaf: THREE.MeshStandardMaterial, trunk: THREE.MeshStandardMaterial): THREE.Group {
  const g = new THREE.Group();
  const t = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 0.28, 5), trunk);
  t.position.y = 0.14;
  const crown = new THREE.Mesh(new THREE.IcosahedronGeometry(0.32, 0), leaf);
  crown.position.y = 0.48;
  crown.castShadow = true;
  g.add(t, crown);
  g.scale.setScalar(0.7 + Math.random() * 0.5);
  return g;
}

function scatterFlowers(root: THREE.Group): void {
  const mats = [0xffffff, 0xffc0cb, 0xfff59d].map(
    (c) => new THREE.MeshStandardMaterial({ color: c, flatShading: true }),
  );
  for (let i = 0; i < 100; i++) {
    const lat = (Math.random() - 0.5) * 1.8;
    const lon = Math.random() * Math.PI * 2;
    const petal = new THREE.Mesh(new THREE.SphereGeometry(0.055, 5, 4), mats[i % mats.length]);
    placeOnSphere(petal, lat, lon, 0.04);
    root.add(petal);
  }
}

function addPathMarkers(root: THREE.Group): void {
  const mat = new THREE.MeshStandardMaterial({ color: PATH_EDGE, flatShading: true, roughness: 0.95 });
  for (let ring = 0; ring < 2; ring++) {
    const count = 42;
    const lat = ring === 0 ? 0.02 : 0.35;
    for (let i = 0; i < count; i++) {
      const lon = (i / count) * Math.PI * 2;
      const stone = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.05, 0.3), mat);
      placeOnSphere(stone, lat, lon, 0.02);
      root.add(stone);
    }
  }
}

export function createClouds(): THREE.Group {
  const root = new THREE.Group();
  root.name = "clouds";
  const mat = new THREE.MeshStandardMaterial({
    color: CLOUD,
    flatShading: true,
    roughness: 1,
    transparent: true,
    opacity: 0.85,
    emissive: 0xffffff,
    emissiveIntensity: 0.18,
  });
  for (let i = 0; i < 18; i++) {
    const cloud = new THREE.Group();
    const n = 3 + (i % 3);
    for (let j = 0; j < n; j++) {
      const puff = new THREE.Mesh(new THREE.IcosahedronGeometry(0.55 + Math.random() * 0.35, 0), mat);
      puff.position.set((j - n / 2) * 0.7, Math.random() * 0.2, Math.random() * 0.3);
      cloud.add(puff);
    }
    const r = PLANET_RADIUS + 5 + Math.random() * 8;
    const lat = (Math.random() - 0.5) * 1.4;
    const lon = Math.random() * Math.PI * 2;
    const x = r * Math.cos(lat) * Math.cos(lon);
    const y = r * Math.sin(lat);
    const z = r * Math.cos(lat) * Math.sin(lon);
    cloud.position.set(x, y, z);
    cloud.lookAt(0, 0, 0);
    cloud.scale.setScalar(0.45 + Math.random() * 0.6);
    root.add(cloud);
  }
  // floating toy islets
  const isletMat = new THREE.MeshStandardMaterial({ color: 0x8bc34a, flatShading: true, roughness: 0.9 });
  const rockMat = new THREE.MeshStandardMaterial({ color: 0x9e9e9e, flatShading: true, roughness: 0.95 });
  for (let i = 0; i < 4; i++) {
    const islet = new THREE.Group();
    const top = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.6, 0.5, 7), isletMat);
    const bottom = new THREE.Mesh(new THREE.ConeGeometry(0.6, 0.9, 7), rockMat);
    bottom.position.y = -0.7;
    bottom.rotation.x = Math.PI;
    islet.add(top, bottom);
    const r = PLANET_RADIUS + 6 + i * 1.6;
    const lat = 0.5 - i * 0.35;
    const lon = i * 1.9 + 0.6;
    islet.position.set(r * Math.cos(lat) * Math.cos(lon), r * Math.sin(lat), r * Math.cos(lat) * Math.sin(lon));
    islet.lookAt(0, 0, 0);
    root.add(islet);
  }
  return root;
}

/** 飘落花瓣 + 萤火：环绕球面漂浮的点粒子 */
export function createAmbientParticles(): { points: THREE.Points; tick: (t: number, dt: number) => void } {
  const count = 260;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const lat0: number[] = [];
  const lon0: number[] = [];
  const lift0: number[] = [];
  const speed0: number[] = [];
  const petal = [new THREE.Color(0xf8bbd0), new THREE.Color(0xfff59d), new THREE.Color(0xffffff)];
  const firefly = new THREE.Color(0xfff176);
  for (let i = 0; i < count; i++) {
    const isFly = i % 5 === 0;
    lat0.push((Math.random() - 0.5) * 2.4);
    lon0.push(Math.random() * Math.PI * 2);
    lift0.push(0.3 + Math.random() * 1.6);
    speed0.push((0.05 + Math.random() * 0.12) * (isFly ? 0.6 : 1));
    const c = isFly ? firefly : (petal[i % petal.length] ?? firefly);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const points = new THREE.Points(
    geo,
    new THREE.PointsMaterial({
      size: 0.14,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      sizeAttenuation: true,
      depthWrite: false,
    }),
  );
  points.name = "ambient";
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const tick = (t: number, dt: number): void => {
    for (let i = 0; i < count; i++) {
      const nextLon = (lon0[i] ?? 0) + (speed0[i] ?? 0) * dt;
      lon0[i] = nextLon;
      const lat = (lat0[i] ?? 0) + Math.sin(t * 0.7 + i) * 0.03;
      const lift = (lift0[i] ?? 0.5) + Math.sin(t * 1.3 + i * 0.7) * 0.12;
      const r = PLANET_RADIUS + lift;
      pos.setXYZ(i, r * Math.cos(lat) * Math.cos(nextLon), r * Math.sin(lat), r * Math.cos(lat) * Math.sin(nextLon));
    }
    pos.needsUpdate = true;
  };
  return { points, tick };
}

function placeOnSphere(obj: THREE.Object3D, lat: number, lon: number, lift: number): void {
  const r = PLANET_RADIUS + lift;
  const x = r * Math.cos(lat) * Math.cos(lon);
  const y = r * Math.sin(lat);
  const z = r * Math.cos(lat) * Math.sin(lon);
  obj.position.set(x, y, z);
  const up = new THREE.Vector3(x, y, z).normalize();
  const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);
  obj.quaternion.copy(quat);
}
